import { useState, useRef, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  IconButton,
  styled,
  useTheme,
  useMediaQuery
} from '@mui/material'
import {
  ArrowBackIos,
  ArrowForwardIos
} from '@mui/icons-material'
import { useContentByEmbed } from '../hooks/useContentByEmbed'
import PropertyCard, { type PropertyData } from '../components/PropertyCard'

// Styled Components for Widget Isolation
const WidgetContainer = styled(Box)(() => ({
  '& *': {
    boxSizing: 'border-box',
  },
  '& .widget-scoped': {
    fontFamily: "'Montserrat', sans-serif",
  },
}))

export interface FeaturedPropertiesCarouselProps {
  embedId: string
}

// Transform API data to PropertyData format
const transformToPropertyData = (item: any): PropertyData => {
  const data = item.data || item
  return {
    id: data.id?.toString() || item.id?.toString() || '',
    image: data.photos?.[0] || data.coverImage || '',
    price: `$${(data.listPrice || data.price || 0).toLocaleString()}`,
    address: data.address || data.title || '',
    city: data.city || '',
    state: data.state || '',
    zipCode: data.postalCode || data.zip || '',
    beds: data.bedrooms || data.numberOfRooms || 0,
    baths: data.bathrooms || data.numberOfBathrooms || 0,
    squareFeet: data.area || data.size || 0,
    isFeatured: data.featured || false,
    isFavorite: false,
    url: data.url || item.url || ''
  }
}

const FeaturedPropertiesCarousel = ({
  embedId
}: FeaturedPropertiesCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [activeCardIndex, setActiveCardIndex] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const cardsTrackRef = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'))
  const baseFilters = useMemo(() => ({ broker: 'Y' }), [])
  const { 
    data: contentData, 
    isLoading: isLoadingContent, 
    error: contentError
  } = useContentByEmbed(embedId, undefined, baseFilters)

  const properties: PropertyData[] = contentData?.data?.data && Array.isArray(contentData.data.data)
    ? contentData.data.data.map(transformToPropertyData)
    : []

  const itemsPerView = isMobile ? 1 : isTablet ? 2 : 3
  const maxIndex = Math.max(0, properties.length - itemsPerView)

  // Update active card and scale/fade effects on scroll (mobile only)
  const updateActiveCard = () => {
    if (!scrollContainerRef.current || !cardsTrackRef.current || !isMobile) return
    
    const container = scrollContainerRef.current
    const track = cardsTrackRef.current
    const cards = track.querySelectorAll('.property-card')
    
    const containerCenter = container.scrollLeft + (container.offsetWidth / 2)
    
    cards.forEach((card, index) => {
      const cardElement = card as HTMLElement
      const cardCenter = cardElement.offsetLeft + (cardElement.offsetWidth / 2)
      const distance = Math.abs(containerCenter - cardCenter)
      
      if (distance < cardElement.offsetWidth / 2) {
        cardElement.style.transform = 'scale(1)'
        cardElement.style.opacity = '1'
        setActiveCardIndex(index)
      } else {
        cardElement.style.transform = 'scale(0.88)'
        cardElement.style.opacity = '0.5'
      }
    })
  }
  
  // Set up scroll listener for mobile
  useEffect(() => {
    const container = scrollContainerRef.current
    if (container && isMobile) {
      container.addEventListener('scroll', updateActiveCard)
      setTimeout(updateActiveCard, 100)
      
      return () => {
        container.removeEventListener('scroll', updateActiveCard)
      }
    }
  }, [isMobile])

  // Scroll to card (mobile)
  const scrollToCard = (index: number) => {
    if (!scrollContainerRef.current || !cardsTrackRef.current) return
    
    const container = scrollContainerRef.current
    const track = cardsTrackRef.current
    const cards = track.querySelectorAll('.property-card')
    
    if (cards[index]) {
      const card = cards[index] as HTMLElement
      const cardLeft = card.offsetLeft
      const containerWidth = container.offsetWidth
      const cardWidth = card.offsetWidth
      const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2)
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      })
    }
  }

  // Desktop navigation
  const nextSlide = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex))
  }

  const prevSlide = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0))
  }
  
  if (isLoadingContent) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center', backgroundColor: 'transparent' }}>
        <Typography variant="body1" color="text.secondary">...</Typography>
      </Paper>
    )
  }

  if (contentError) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center', backgroundColor: 'transparent' }}>
        <Typography variant="body1" color="error">
          Error loading properties: {contentError.message}
        </Typography>
      </Paper>
    )
  }

  if (properties.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center', backgroundColor: 'transparent' }}>
        <Typography variant="body1" color="text.secondary">No properties available</Typography>
      </Paper>
    )
  }

  const handleViewDetails = (property: PropertyData) => {
    if (property.url) {
      window.location.href = property.url
    }
  }

  const visibleProperties = properties.slice(currentIndex, currentIndex + itemsPerView)

  return (
    <WidgetContainer 
      id="featured-properties-widget"
      className="widget-scoped"
      sx={{
        width: '100%',
        backgroundColor: 'transparent',
        p: 0,
        m: 0,
        position: 'relative',
        overflow: { xs: 'hidden', md: 'visible' },
        '& *': {
          fontFamily: "'Montserrat', sans-serif",
        }
      }}
    >
      <link 
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap" 
        rel="stylesheet" 
      />
      
      <Box sx={{ position: 'relative', px: { xs: 0, md: 6, lg: 8 } }}>
        {/* Navigation Arrows - Desktop only */}
        {!isMobile && (
          <>
            <IconButton
              onClick={prevSlide}
              disabled={currentIndex === 0}
              sx={{
                position: 'absolute',
                left: { md: -12, lg: -21 },
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                color: '#b08b4f',
                zIndex: 10,
                width: 42,
                height: 42,
                borderRadius: '50%',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  backgroundColor: '#b08b4f',
                  color: 'white'
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }
              }}
            >
              <ArrowBackIos />
            </IconButton>

            <IconButton
              onClick={nextSlide}
              disabled={currentIndex >= maxIndex}
              sx={{
                position: 'absolute',
                right: { md: -12, lg: -21 },
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                color: '#b08b4f',
                zIndex: 10,
                width: 42,
                height: 42,
                borderRadius: '50%',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  backgroundColor: '#b08b4f',
                  color: 'white'
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }
              }}
            >
              <ArrowForwardIos />
            </IconButton>
          </>
        )}

        {/* Mobile: Horizontal scroll with scale effect */}
        {isMobile ? (
          <Box 
            ref={scrollContainerRef}
            sx={{ 
              overflowX: 'scroll',
              overflowY: 'visible',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              padding: '20px 0',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }}
          >
            <Box
              ref={cardsTrackRef}
              sx={{
                display: 'flex',
                gap: 1.5,
                paddingLeft: '10vw',
                paddingRight: '10vw',
                paddingTop: '20px',
                paddingBottom: '20px',
              }}
            >
              {properties.map((property, index) => (
                <Box
                  key={property.id}
                  className="property-card"
                  sx={{
                    minWidth: '80vw',
                    maxWidth: '80vw',
                    flex: '0 0 80vw',
                    scrollSnapAlign: 'center',
                    scrollSnapStop: 'always',
                    transition: 'transform 0.4s ease, opacity 0.4s ease',
                    transform: index === 0 ? 'scale(1)' : 'scale(0.88)',
                    opacity: index === 0 ? 1 : 0.5,
                  }}
                >
                  <PropertyCard 
                    data={property} 
                    onViewDetails={handleViewDetails}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          /* Desktop: Grid layout */
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: 3,
              px: { md: 2, lg: 3 }
            }}
          >
            {visibleProperties.map((property) => (
              <Box key={property.id}>
                <PropertyCard 
                  data={property} 
                  onViewDetails={handleViewDetails}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Dots Indicator - Mobile */}
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.25, mt: 2.5 }}>
          {properties.map((_, index) => (
            <Box
              key={index}
              onClick={() => scrollToCard(index)}
              sx={{
                width: index === activeCardIndex ? 12 : 10,
                height: index === activeCardIndex ? 12 : 10,
                borderRadius: '50%',
                backgroundColor: index === activeCardIndex ? '#b08b4f' : 'rgba(0,0,0,0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: index === activeCardIndex ? '#b08b4f' : 'rgba(0,0,0,0.5)'
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Dots Indicator - Desktop */}
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
          {Array.from({ length: maxIndex + 1 }, (_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: index === currentIndex ? '#b08b4f' : 'rgba(0,0,0,0.3)',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                '&:hover': {
                  backgroundColor: index === currentIndex ? '#b08b4f' : 'rgba(0,0,0,0.5)'
                }
              }}
            />
          ))}
        </Box>
      )}
    </WidgetContainer>
  )
}

export default FeaturedPropertiesCarousel
